import React, { FC } from 'react';
import { Autocomplete, Box, FormControl, TextField, ToggleButtonGroup, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import ArrowDateFilter from 'components/shared/arrow-date-filter/arrow-date-filter.component';
import { StyledToggleButton } from 'components/shared/styled-toggle-button/styled-toggle-button.component';
import { getClinicsForLabDropdownTemplate } from 'apollo/templates/organization';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { Organization } from 'models/organization';
import { ProductionOverviewCountMode, ProductionOverviewPeriod } from 'models/production-overview';
import { getDatePickerFormat, getDateFnsLocale } from 'utils/format';
import { CustomRangeValidationError } from './production-overview-utils';

export type OverviewFilterState = {
    period: ProductionOverviewPeriod;
    startDate: Date;
    customFromDate: Date | null;
    customToDate: Date | null;
    clinic: Organization | null;
    countMode: ProductionOverviewCountMode;
};

type OverviewFilterBarProps = {
    filters: OverviewFilterState;
    onFiltersChange: (filters: OverviewFilterState) => void;
    rangeValidationError: CustomRangeValidationError;
};

const OverviewFilterBar: FC<OverviewFilterBarProps> = ({
    filters,
    onFiltersChange,
    rangeValidationError,
}: OverviewFilterBarProps) => {
    const { t: translate } = useTranslation(['production-overview', 'common']);
    const { showBoundary } = useDenthubErrorBoundary();

    const { data: { clinicsForLab } = {}, loading: isClinicListLoading } = useQuery(getClinicsForLabDropdownTemplate, {
        variables: { filters: { showParents: true } },
        onError(error) {
            showBoundary(error);
        },
    });

    const handlePeriodChange = (_: React.MouseEvent, period: ProductionOverviewPeriod | null) => {
        if (period && period !== filters.period) onFiltersChange({ ...filters, period });
    };

    const handleCountModeChange = (_: React.MouseEvent, countMode: ProductionOverviewCountMode | null) => {
        if (countMode && countMode !== filters.countMode) onFiltersChange({ ...filters, countMode });
    };

    const handleClinicChange = (_: React.SyntheticEvent, clinic: Organization | null) => {
        onFiltersChange({ ...filters, clinic });
    };

    const isCustomPeriod = filters.period === ProductionOverviewPeriod.CUSTOM;

    return (
        <Box mb={1} display="flex" alignItems="center" flexWrap="wrap" gap={2}>
            {!isCustomPeriod ? (
                <ArrowDateFilter
                    dateValue={filters.startDate}
                    onDateFilterChange={(date) => date && onFiltersChange({ ...filters, startDate: date })}
                    label={translate('common:DATE')}
                />
            ) : (
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={getDateFnsLocale()}>
                    <Box display="flex" gap={1} alignItems="center">
                        <FormControl margin="dense">
                            <DatePicker
                                format={getDatePickerFormat()}
                                value={filters.customFromDate}
                                label={translate('production-overview:CUSTOM_RANGE_FROM')}
                                onChange={(date) => onFiltersChange({ ...filters, customFromDate: date })}
                            />
                        </FormControl>
                        <FormControl margin="dense">
                            <DatePicker
                                format={getDatePickerFormat()}
                                value={filters.customToDate}
                                label={translate('production-overview:CUSTOM_RANGE_TO')}
                                onChange={(date) => onFiltersChange({ ...filters, customToDate: date })}
                            />
                        </FormControl>
                    </Box>
                </LocalizationProvider>
            )}

            <ToggleButtonGroup
                value={filters.period}
                exclusive
                onChange={handlePeriodChange}
                aria-label={translate('production-overview:PERIOD')}
            >
                <StyledToggleButton value={ProductionOverviewPeriod.FOURTEEN_DAYS}>
                    {translate('production-overview:PERIOD_14_DAYS')}
                </StyledToggleButton>
                <StyledToggleButton value={ProductionOverviewPeriod.THIRTY_DAYS}>
                    {translate('production-overview:PERIOD_30_DAYS')}
                </StyledToggleButton>
                <StyledToggleButton value={ProductionOverviewPeriod.CUSTOM}>
                    {translate('production-overview:PERIOD_CUSTOM')}
                </StyledToggleButton>
            </ToggleButtonGroup>

            <Autocomplete
                sx={{ minWidth: 320 }}
                loading={isClinicListLoading}
                id="production-overview-clinic-dropdown"
                options={clinicsForLab || []}
                onChange={handleClinicChange}
                value={(filters.clinic || { name: '' }) as Organization}
                getOptionLabel={(option: Organization) => option?.name || ''}
                renderOption={(props, option: Organization) => (
                    <li {...{ ...props, key: option?._id }}>
                        {option.name} &nbsp;
                        {!option.parentClinicId && !option.local && option.isMain ? (
                            <i> ({translate('common:MAIN')})</i>
                        ) : undefined}
                    </li>
                )}
                isOptionEqualToValue={(option: Organization, value: Organization) => option?._id === value?._id}
                renderInput={(params) => (
                    <TextField {...params} label={translate('common:CLINIC')} fullWidth={true} margin="dense" />
                )}
            />

            <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2" color="text.secondary">
                    {translate('production-overview:SHOW_COUNT')}
                </Typography>
                <ToggleButtonGroup
                    value={filters.countMode}
                    exclusive
                    onChange={handleCountModeChange}
                    aria-label={translate('production-overview:SHOW_COUNT')}
                >
                    <StyledToggleButton value={ProductionOverviewCountMode.ORDERS}>
                        {translate('production-overview:COUNT_MODE_ORDERS')}
                    </StyledToggleButton>
                    <StyledToggleButton value={ProductionOverviewCountMode.ACTIONS}>
                        {translate('production-overview:COUNT_MODE_ACTIONS')}
                    </StyledToggleButton>
                </ToggleButtonGroup>
            </Box>

            {isCustomPeriod && rangeValidationError ? (
                <Typography variant="body2" color="error" data-test="production-overview.range-validation-message">
                    {translate(`production-overview:${rangeValidationError}`)}
                </Typography>
            ) : undefined}
        </Box>
    );
};

export default OverviewFilterBar;
